// utils/SimpleVisitCounter.js
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaEye } from 'react-icons/fa';
import './SimpleVisitCounter.css';

/**
 * 실시간 방문자 카운터 컴포넌트 - 어느 페이지에나 쉽게 추가 가능
 * @param {string} pageName - 방문자 수를 표시할 페이지 이름 (기본값: 현재 페이지)
 * @param {string} language - 언어 설정
 * @param {string} SERVER_ROOT - 서버 URL
 * @param {number} refreshInterval - 갱신 간격(밀리초), 기본값 30초
 * @param {string} userEmail - 현재 사용자의 이메일 (관리자 제외 여부 확인용)
 * @param {string} userIp - 현재 사용자의 IP 주소 (로컬 IP 제외 여부 확인용)
 * @param {boolean} excludeLocalIp - 로컬 IP 제외 여부
 * @param {boolean} excludeAdminEmail - 관리자 이메일 제외 여부
 */
const SimpleVisitCounter = ({ 
  pageName = null, 
  language, 
  SERVER_ROOT,
  refreshInterval = 30000, // 기본 30초마다 갱신
  userEmail = '',
  userIp = '',
  excludeLocalIp = true,
  excludeAdminEmail = true
}) => {
  const [visitStats, setVisitStats] = useState({
    total: 0,
    today: 0,
    loading: true
  });
  
  // 현재 페이지 이름 저장용 ref
  const currentPageRef = useRef('');
  
  // 주기적 폴링을 위한 타이머 ID 저장
  const timerRef = useRef(null);

  // AbortController 참조 저장
  const abortControllerRef = useRef(null);
  
  // 방문자 통계 가져오는 함수
  const fetchVisitStats = async () => {
    // 이전 요청이 진행 중이면 취소
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // 새 AbortController 생성
    abortControllerRef.current = new AbortController();
    
    try {
      // 현재 페이지 이름 결정
      let currentPage = pageName;
      if (!currentPage) {
        const path = window.location.pathname;
        if (path === '/') {
          currentPage = 'Home';
        } else {
          const pathWithoutSlash = path.substring(1);
          currentPage = pathWithoutSlash.charAt(0).toUpperCase() + pathWithoutSlash.slice(1);
        }
      }
      
      // 페이지 이름 저장
      currentPageRef.current = currentPage;

      // 필터링 옵션 설정
      const filterOptions = {
        excludeLocalIp: excludeLocalIp ? '1' : '0',
        excludeAdminEmail: excludeAdminEmail ? '1' : '0'
      };

      // 총 방문자 수 가져오기
      const summaryResponse = await axios.get(`${SERVER_ROOT}/api/visit-stats`, {
        params: { 
          userEmail: 'gategil1149@gmail.com',
          period: 'summary',
          page_name: currentPage,
          ...filterOptions
        },
        signal: abortControllerRef.current.signal // AbortController의 signal 전달
      });

      // 한국 시간으로 오늘 날짜 구하기
      const now = new Date();
      const koreaTime = new Date(now.getTime() + (9 * 60 * 60 * 1000));
      const todayDate = koreaTime.toISOString().split('T')[0]; // YYYY-MM-DD
      
      // 일별 방문자 데이터 가져오기
      const dailyResponse = await axios.get(`${SERVER_ROOT}/api/visit-stats`, {
        params: { 
          userEmail: 'gategil1149@gmail.com', 
          period: 'daily',
          page_name: currentPage,
          ...filterOptions
        },
        signal: abortControllerRef.current.signal // AbortController의 signal 전달
      });
      
      // 컴포넌트가 언마운트되었는지 확인
      if (abortControllerRef.current === null) return;
      
      // 오늘 날짜와 정확히 일치하는 항목 찾기
      const todayStats = dailyResponse.data.find(entry => { 
        const entryDateKST = new Date(entry.visit_date);
        entryDateKST.setHours(entryDateKST.getHours() + 9); // UTC to KST
        const entryDate = entryDateKST.toISOString().substring(0, 10);
        return entryDate === todayDate;
      });
      
      // 통계 업데이트
      setVisitStats({
        total: summaryResponse.data[0]?.total_visits || 0,
        today: todayStats?.total_visits || 0,
        loading: false
      });
    } catch (error) {
      // AbortError는 정상적인 취소이므로 에러 로그를 표시하지 않음
      if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
        console.error('Error fetching visit statistics:', error);
      }
      
      // 컴포넌트가 마운트된 상태일 때만 상태 업데이트
      if (abortControllerRef.current !== null) {
        setVisitStats(prev => ({
          ...prev,
          loading: false
        }));
      }
    }
  };

  // 컴포넌트 마운트 시 초기 데이터 로드 및 주기적 업데이트 설정
  useEffect(() => {
    // 초기 데이터 로드
    fetchVisitStats();
    
    // 주기적인 데이터 갱신 설정
    timerRef.current = setInterval(fetchVisitStats, refreshInterval);
    
    // 컴포넌트 언마운트 시 타이머 정리 및 진행 중인 요청 취소
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      
      // 진행 중인 요청 취소
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null; // 참조 제거
      }
    };
  }, [pageName, SERVER_ROOT, refreshInterval, excludeLocalIp, excludeAdminEmail]);

  // 페이지 포커스 시 데이터 새로고침 (탭 전환 후 돌아올 때)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchVisitStats();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  if (visitStats.loading) {
    return <div className="simple-visit-counter-loading"></div>;
  }

  // 로그인된 사용자가 제외 대상인지 확인
  const isExcluded = (excludeLocalIp && userIp === '127.0.0.1') || 
                     (excludeAdminEmail && userEmail === 'gategil1149@gmail.com');

  return (
    <div className="simple-visit-counter">
      <div className="visit-stats">
        <div className="visit-item">
          <FaEye className="visit-icon" />
          <span className="visit-label">
            {language === 'KR' ? '총 방문' : 'Total'}:
          </span>
          <span className="visit-value">{visitStats.total.toLocaleString()}</span>
        </div>
        <div className="visit-item">
          <FaEye className="visit-icon" />
          <span className="visit-label">
            {language === 'KR' ? '오늘' : 'Today'}:
          </span>
          <span className="visit-value">{visitStats.today.toLocaleString()}</span>
        </div>
        
        {/* 현재 로그인한 사용자가 통계에서 제외되고 있는지 표시 (선택적) */}
        {isExcluded && (
          <div className="exclusion-note">
            <span className="exclusion-badge">
              {language === 'KR' ? '개발자 제외' : 'Not Counted'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleVisitCounter;